import { SERVICE_REQUEST_STATUS } from "../store/admin/investorOperations/constants";
import { normalizeTimelineItems } from "./timeline";

const CANCELABLE = new Set([
  SERVICE_REQUEST_STATUS.SUBMITTED,
  SERVICE_REQUEST_STATUS.AWAITING_CONTACT,
  SERVICE_REQUEST_STATUS.IN_PROGRESS,
  SERVICE_REQUEST_STATUS.AWAITING_CUSTOMER,
]);

const NEXT_STEP = {
  [SERVICE_REQUEST_STATUS.SUBMITTED]: "Your property request has been received.",
  [SERVICE_REQUEST_STATUS.AWAITING_CONTACT]: "Our team will contact you shortly.",
  [SERVICE_REQUEST_STATUS.IN_PROGRESS]: "Your request is being reviewed.",
  [SERVICE_REQUEST_STATUS.AWAITING_CUSTOMER]: "We are waiting for your response or documents.",
  [SERVICE_REQUEST_STATUS.INSPECTION_SCHEDULED]: "Your property inspection has been scheduled.",
  [SERVICE_REQUEST_STATUS.INSPECTION_COMPLETED]: "Your inspection has been completed.",
  [SERVICE_REQUEST_STATUS.APPROVED]: "Your request has been approved for the next stage.",
  [SERVICE_REQUEST_STATUS.COMPLETED]: "Your property request is complete.",
  [SERVICE_REQUEST_STATUS.DECLINED]: "This request could not proceed.",
  [SERVICE_REQUEST_STATUS.CANCELLED]: "This request has been cancelled.",
};

export function canCancelRealEstateRequest(status) {
  return CANCELABLE.has(status);
}

export function getRealEstateNextStepCopy(status) {
  return NEXT_STEP[status] || "Your request status will update as our team progresses.";
}

export function mapInspection(raw) {
  if (!raw) return null;
  return {
    id: raw.id,
    date: raw.date || null,
    time: raw.time || null,
    location: raw.location || null,
    assignedStaffId: raw.assigned_staff_id ?? null,
    assignedStaffName: raw.assigned_staff_name || null,
    note: raw.note || null,
    status: raw.status || null,
    customerConfirmed: Boolean(raw.customer_confirmed),
    customerConfirmedAt: raw.customer_confirmed_at || null,
    completedAt: raw.completed_at || null,
  };
}

export function mapApplicantTimeline(entries = []) {
  return normalizeTimelineItems(entries);
}

/**
 * Normalize applicant API detail/list item for portal UI + My Requests list shape.
 */
export function mapInvestorRealEstateRequest(raw) {
  if (!raw) return null;

  const re = raw.real_estate || {};
  const propertyName = re.property_name || raw.title || "Property request";
  const units = re.units_requested ?? null;
  const estimatedValue = raw.estimated_value_ngn != null ? Number(raw.estimated_value_ngn) : null;
  const submittedAt = raw.submitted_at || raw.created_at || null;
  const status = raw.status;
  const reference = raw.request_reference;
  const inspection = mapInspection(raw.inspection);

  return {
    id: raw.id,
    type: "real_estate",
    accountType: raw.account_type || "investor",
    reference,
    request_reference: reference,
    status,
    title: propertyName,
    estimatedValueNgn: estimatedValue,
    preferredContact: raw.preferred_contact || null,
    customerNote: raw.customer_note || null,
    submittedAt,
    created_at: submittedAt,
    updatedAt: raw.updated_at || null,
    canCancel: canCancelRealEstateRequest(status),
    nextStep: getRealEstateNextStepCopy(status),
    inspection,
    timeline: mapApplicantTimeline(raw.timeline),
    realEstate: {
      propertyId: re.property_id || null,
      propertyName: re.property_name || propertyName,
      location: re.location || null,
      unitsRequested: units,
      inspectionPreference: re.inspection_preference || null,
      documentationInterest: Boolean(re.documentation_interest),
      requestNote: re.request_note || null,
    },
    // My Requests / list card compatibility
    payload: {
      title: propertyName,
      name: propertyName,
      units,
      estimatedValueNgn: estimatedValue,
      location: re.location || null,
      contact_method: raw.preferred_contact || null,
      preferredContact: raw.preferred_contact || null,
    },
    createdAt: submittedAt,
  };
}

export function buildCreateRealEstatePayload(values, property = null) {
  const units = Number(values.units || values.units_requested || 1);
  const pricePerUnit = Number(property?.pricePerUnitNgn || 0);
  const estimated =
    values.estimated_value_ngn != null
      ? Number(values.estimated_value_ngn)
      : pricePerUnit > 0
        ? units * pricePerUnit
        : undefined;

  return {
    title: property?.name || values.title || values.property_name,
    property_id: property?.id || values.property_id || undefined,
    property_name: property?.name || values.property_name || values.title,
    location: property?.location || values.location || undefined,
    units_requested: Number.isFinite(units) && units >= 1 ? units : 1,
    estimated_value_ngn: estimated != null && !Number.isNaN(estimated) ? estimated : undefined,
    inspection_preference: values.inspection_preference || undefined,
    documentation_interest: Boolean(values.documentation_interest),
    preferred_contact: values.preferred_contact || values.contact_method || undefined,
    full_name: values.full_name || values.requester_name || undefined,
    phone: values.phone || undefined,
    email: values.email || undefined,
    customer_note: values.customer_note || values.note || values.message || undefined,
    request_note: values.request_note || undefined,
  };
}
